import {
  OrderFlow,
  type Order,
  type OrderType,
} from "@/types/api/contracts/orders";
import type { IAPI } from "./APIService";
import type { AxiosResponse } from "axios";
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  useQueries,
} from "@tanstack/react-query";
import { buildQueryKey } from "@/utils/query";
import type { VenueSpecific } from "@/types/api/venue";
import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type { PastOrderStatus } from "@/components/screens/PastOrdersTransactions/Orders/List/Types/PastOrdersTypes";
import type { IItem } from "./Operations/Items";
import type { Slot } from "@/types/api/contracts/slots";
import type { IModifier } from "./Operations/Modifiers";
import type { PastTransactionsCardType, PaymentType } from "./Transactions";
import type {
  CourseThemeEnum,
  OrderTrackingAction,
} from "@/components/screens/PastOrdersTransactions/OrderModal/Types/OrderModalTypes";
import type {
  PaymentCardReadMethod,
  PaymentWalletType,
} from "@/components/screens/PastOrdersTransactions/Types/PastOrdersTransactionsTypes";

export interface IOrderParams extends VenueSpecific {
  status?: Array<PastOrderStatus>;
  page?: number;
  page_size?: number;
  sort?: string;
  slot_id?: number;
  order_type?: OrderType;
  include?: string;
  payee_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface IOrderList extends APIPaginatedResponse<Order> {}

export interface IBillPayee {
  bill_amount: number;
  total_amount?: number;
  captured: boolean;
  captured_date: string;
  delivery_fee_amount: number;
  id: number;
  payee_email: null | string;
  payment_method: number;
  refunded: boolean;
  refunded_amount: null | number;
  refunded_date: null | string;
  small_order_amount: number;
  tip: number;
  transaction_fee_amount: number;
  vat_amount: number;
}

interface IRestaurant {
  address1: string;
  bill_input_type: number;
  cashback_percentage: number;
  configuration_id: null | number;
  discount_on_full_bill: boolean;
  id: number;
  name: string;
  postcode: string;
  service_charge_on_discount: boolean;
  service_charge_option: number;
  service_mandatory: boolean;
  service_min_covers: number;
  service_percentage: number;
  thank_you_message: string;
  timezone: string;
  tipping_enabled: boolean;
  town: string;
  vat_number: string;
}

export interface IBillItem extends Partial<IItem> {
  modifiers: Array<IModifier>;
  refunded_quantity?: number;
  onDecrementQuantity?: () => void;
  onIncrementQuantity?: () => void;
  total_value_with_modifiers: number;
  quantity: number;
  refunded: boolean;
  void: boolean;
  category_id: number;
  category_name: string;
  category_order: number;
  special_requests: string;
  allergy_alerts: Array<{
    id: number;
    name: string;
  }>;
  course: {
    id: number;
    name: string;
    order: number;
    theme_colour: CourseThemeEnum;
  };
}

export interface IOrderWithBill extends Order {
  bill: {
    adjustment_amount: number;
    bill_status: number;
    bill_type: number;
    billpayee_set: Array<IBillPayee>;
    delivery_fee_amount: number;
    discount_amount_currency: null;
    discount_code: {
      code: string;
      id: number;
      message: string;
    };
    id: number;
    menu_items: Array<IBillItem>;
    number_of_heads: number;
    prepaid_tab: {
      code: string;
      id: number;
      message: string;
    };
    prepaid_tab_amount_currency: null;
    refunded_amount: number | null;
    restaurant: IRestaurant;
    service_charge_amount: number;
    small_order_amount: number;
    total_amount: number;
    total_amount_left_to_pay: number;
    transaction_fee_amount: number;
    vat_amount: number;
    tip_amount: number;
  };
  order_statuses: Array<{
    id: number;
    created_at: string;
    rejection_reason: null | string;
    status: PastOrderStatus;
  }>;
  payees: IPayeesData;
}

export interface ISinglePayee {
  captured: boolean;
  captured_date: string;
  card_last4: null | number | string;
  cardholder_name: null | string;
  id: number;
  payment_type: PaymentType;
  pending: boolean;
  refunded: boolean;
  refunded_amount: number | null;
  refunded_date: string | null;
  total_amount: number;
  tip: number;
  transaction_intent_client_secret: null | string | number;
  transaction_intent_id: null | string | number;
  bill_amount?: number;
  card_read_method: PaymentCardReadMethod;
  wallet_type: PaymentWalletType;
  card_type: PastTransactionsCardType;
}

export interface IPayeesData {
  metadata: {
    total_amount: number;
    total_amount_left_to_pay: number;
  };
  payees: Array<ISinglePayee>;
}

export interface IPrintOrderParams {
  order_identifier: string;
  printer_id: string;
  isVenueSpecific?: boolean;
}
export interface ISendReceiptParams {
  order_identifier: string;
  email: string;
  isVenueSpecific?: boolean;
}

export interface IUpdateCommentParams {
  order_identifier: string;
  comment: string;
  isVenueSpecific?: boolean;
}

export interface IEmployeeOrderAction {
  id: number;
  model_name: string;
  model_pk: number;
  restaurant_id: number;
  employee_id: number | null;
  employee_name_snapshot: string | null;
  grant_employee_id: number | null;
  grant_employee_name_snapshot: string | null;
  action: OrderTrackingAction;
  action_time: string;
  details: {
    identifier: string;
  };
}

export interface IEmployeeOrderActionResponse
  extends APIPaginatedResponse<IEmployeeOrderAction> {}

export interface IGetEmployeeSlotSessionViaActionParams {
  action: OrderTrackingAction;
  id: string | number;
  model: string;
  isVenueSpecific?: boolean;
}

export interface IOrderListOptions {
  enabled?: boolean;
}

export interface IRefundPayeeParams {
  order_identifier: string;
  payee_id: number;
  isVenueSpecific?: boolean;
}

export interface ICustomRefundPayeeParams {
  bill_payee_id: number;
  bill_menu_items: Array<{
    id: number;
    quantity: number;
  }>;
  refund_amount: number;
  custom_refund?: boolean;
  order_identifier: string;
  delivery_fee_amount?: number;
  transaction_fee_amount?: number;
  service_amount?: number;
  small_order_amount?: number;
  tip_amount?: number;
  isVenueSpecific?: boolean;
}

export interface IRefundOrderParams {
  order_identifier: string;
  isVenueSpecific?: boolean;
}

export interface IUpdateStatusParams {
  order_identifier: string;
  status: PastOrderStatus;
  isVenueSpecific?: boolean;
}

export interface IUpdateOrderParams {
  order_identifier: string;
  estimated_delivery_time?: string;
  isVenueSpecific?: boolean;
}

export interface IGetEmployeeOrderActionsParams {
  id?: ID;
  isVenueSpecific?: boolean;
}

export interface IGetMultipleEmployeeActionsViaModelParams {
  action: OrderTrackingAction;
  ids: Array<ID>;
  model: string;
  isVenueSpecific?: boolean;
}

export interface IGetOrderParams {
  id?: ID;
  isVenueSpecific?: boolean;
}

export interface IOrderService {
  list: (
    params: IOrderParams,
    options?: IOrderListOptions
  ) => UseQueryResult<IOrderList, Error>;
  get: (params: IGetOrderParams) => UseQueryResult<IOrderWithBill, Error>;
  getOrderWithSlot: (
    params: IGetOrderParams
  ) => UseQueryResult<IOrderWithBill, Error>;
  printOrder: () => UseMutationResult<number, Error, IPrintOrderParams>;
  sendReceipt: () => UseMutationResult<void, Error, ISendReceiptParams>;
  updateComment: () => UseMutationResult<
    IOrderWithBill,
    Error,
    IUpdateCommentParams
  >;
  exportOrders: (
    params: IOrderParams,
    enabled: boolean
  ) => UseQueryResult<string, Error>;

  // Update
  update: () => UseMutationResult<void, Error, IUpdateOrderParams>;
  updateStatus: () => UseMutationResult<void, Error, IUpdateStatusParams>;

  // Actions
  getEmployeeOrderActions: (
    params: IGetEmployeeOrderActionsParams
  ) => UseQueryResult<Array<IEmployeeOrderAction>, Error>;

  getMultipleEmployeeOrderActionsViaModel: (
    params: IGetMultipleEmployeeActionsViaModelParams
  ) => Array<UseQueryResult<Array<IEmployeeOrderAction>, Error>>;

  getOrderActionViaModel: (
    params: IGetEmployeeSlotSessionViaActionParams
  ) => UseQueryResult<Array<IEmployeeOrderAction>, Error>;

  // Refunds
  refundOrder: () => UseMutationResult<void, Error, IRefundOrderParams>;
  refundPayee: () => UseMutationResult<void, Error, IRefundPayeeParams>;
  customRefundPayee: () => UseMutationResult<
    void,
    Error,
    ICustomRefundPayeeParams
  >;
}

export class OrderService implements IOrderService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params: IOrderParams,
    options?: IOrderListOptions
  ): UseQueryResult<IOrderList, Error> {
    const { venueSpecific, ...restParams } = params;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["orders", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        if (options && !options?.enabled) {
          return {
            results: [],
            next: null,
            previous: null,
            count: 0,
          };
        }

        const {
          data: { results = [], next, previous, count },
        } = await this.api.get<AxiosResponse<IOrderList>>("/orders/", {
          params: restParams,
          endpointService: EndpointService.Gateway,
          venueSpecific: venueSpecific ?? false,
        });

        return {
          results,
          next,
          previous,
          count,
        };
      },
    });
  }

  get(params: IGetOrderParams): UseQueryResult<IOrderWithBill, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["orders", "get", params.id],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        if (!params.id) {
          return null;
        }

        const { data } = await this.api.get<AxiosResponse<IOrderWithBill>>(
          `/orders/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            bustCache: true,
            venueSpecific: params.isVenueSpecific,
          }
        );

        return data;
      },
    });
  }

  getOrderWithSlot(
    params: IGetOrderParams
  ): UseQueryResult<IOrderWithBill, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["orders", "get", params.id],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        if (!params.id) {
          return null;
        }

        const { data: order } = await this.api.get<
          AxiosResponse<IOrderWithBill>
        >(`/orders/${params.id}/`, {
          endpointService: EndpointService.Gateway,
          bustCache: true,
          venueSpecific: params.isVenueSpecific,
        });

        if (order.slot) {
          const { data: slot } = await this.api.get<AxiosResponse<Slot>>(
            `/slots/${order.slot}/`,
            {
              endpointService: EndpointService.Gateway,
              bustCache: true,
              venueSpecific: params.isVenueSpecific,
            }
          );

          order.slot = slot;
        }

        if (order.order_flow === OrderFlow.EPOS) {
          const { data } = await this.api.get<AxiosResponse<IPayeesData>>(
            `/orders/${params.id}/payment/`,
            {
              endpointService: EndpointService.Core,
              venueSpecific: params.isVenueSpecific,
            }
          );

          order.payees = data;
        }

        return order;
      },
    });
  }

  printOrder(): UseMutationResult<number, Error, IPrintOrderParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "print"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { order_identifier, printer_id, isVenueSpecific = true } = params;
        const response = await this.api.post<AxiosResponse<void>>(
          `/orders/${order_identifier}/print/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: {
              printer_id,
            },
          }
        );

        return response.status;
      },
      onMutate: async (newOrder) => {},
      onSuccess: async (data) => {},
      onError: async (error) => {},
    });
  }

  sendReceipt(): UseMutationResult<void, Error, ISendReceiptParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "sendReceipt"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { order_identifier, email, isVenueSpecific = true } = params;
        await this.api.post<AxiosResponse<void>>(
          `/orders/${order_identifier}/receipt/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: {
              email,
            },
          }
        );
      },
      onMutate: async (newOrder) => {},
      onSuccess: async (data) => {},
      onError: async (error) => {},
    });
  }

  updateComment(): UseMutationResult<
    IOrderWithBill,
    Error,
    IUpdateCommentParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "updateComment"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { order_identifier, comment, isVenueSpecific = false } = params;
        const { data: order } = await this.api.patch<
          AxiosResponse<IOrderWithBill>
        >(`/orders/${order_identifier}`, {
          endpointService: EndpointService.Core,
          venueSpecific: isVenueSpecific,
          params: {
            comment,
          },
        });

        return order;
      },
      onMutate: async (params) => {
        const { order_identifier, comment } = params;

        // Optimistically update the order in the cache
        await queryClient.cancelQueries({
          queryKey: ["orders", "get", order_identifier],
        });

        const previousOrder = queryClient.getQueryData<IOrderWithBill>([
          "orders",
          "get",
          order_identifier,
        ]);

        if (previousOrder) {
          queryClient.setQueryData(["orders", "get", order_identifier], {
            ...previousOrder,
            comment, // Update the comment optimistically
          });
        }

        return { previousOrder }; // Return the previous order for rollback if needed
      },
      onError: (err, params, context) => {
        // Rollback to the previous order data in case of error
        if (context?.previousOrder) {
          queryClient.setQueryData(
            ["orders", "get", params.order_identifier],
            context.previousOrder
          );
        }
      },
      onSettled: (data, error, params) => {
        // Refetch the order data after mutation
        queryClient.invalidateQueries({
          queryKey: ["orders", "get", params.order_identifier],
        });
      },
    });
  }

  exportOrders(
    params: IOrderParams,
    enabled: boolean
  ): UseQueryResult<string, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value != null || value !== ""
      )
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["orders", "export"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<string>>(
          "/orders/csv/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: params.venueSpecific,
            params: updatedParams,
          }
        );

        return data;
      },
      enabled,
    });
  }

  // Update
  update(): UseMutationResult<void, Error, IUpdateOrderParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params: IUpdateOrderParams) => {
        const {
          order_identifier,
          isVenueSpecific = false,
          ...restParams
        } = params;
        await this.api.patch<AxiosResponse<void>>(
          `/orders/${order_identifier}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: isVenueSpecific,
            params: restParams,
          }
        );
      },
      onMutate: async (params) => {},
      onSettled: (data, error, params) => {
        // Refetch the order data after mutation
        queryClient.invalidateQueries({
          queryKey: ["orders", "get", params.order_identifier],
        });
      },
      onError: async (error) => {},
    });
  }

  updateStatus(): UseMutationResult<void, Error, IUpdateStatusParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "updateStatus"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { order_identifier, status, isVenueSpecific = false } = params;
        await this.api.patch<AxiosResponse<void>>(
          `/orders/${order_identifier}/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: isVenueSpecific,
            params: {
              status,
            },
          }
        );
      },
      onMutate: async (params) => {},
      onSettled: (data, error, params) => {
        // Refetch the order data after mutation
        queryClient.invalidateQueries({
          queryKey: ["orders", "get", params.order_identifier],
        });
      },
      onError: async (error) => {},
    });
  }

  // Actions
  getEmployeeOrderActions(
    params: IGetEmployeeOrderActionsParams
  ): UseQueryResult<Array<IEmployeeOrderAction>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["orders", "employee-order-actions", params.id],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        if (!params.id) {
          return null;
        }

        const { data } = await this.api.get<
          AxiosResponse<IEmployeeOrderActionResponse>
        >(`/tracking/?pk=${params.id}&model=Order`, {
          params: {
            page_size: 50,
          },
          endpointService: EndpointService.Core,
          venueSpecific: params.isVenueSpecific,
        });

        return data.results;
      },
    });
  }

  getMultipleEmployeeOrderActionsViaModel(
    params: IGetMultipleEmployeeActionsViaModelParams
  ): Array<UseQueryResult<Array<IEmployeeOrderAction>, Error>> {
    const userQueries = useQueries({
      queries: params.ids.map((id) => ({
        queryKey: buildQueryKey({
          queryKey: ["orders", "employee-order-actions", id],
          venueID: this.api.selectedVenue,
          params: {},
        }),
        queryFn: async () => {
          if (!id) {
            return null;
          }

          const { data } = await this.api.get<
            AxiosResponse<IEmployeeOrderActionResponse>
          >(
            `/tracking/?pk=${id}&model=${params.model}&action=${params.action}`,
            {
              params: {
                page_size: 50,
              },
              endpointService: EndpointService.Core,
              venueSpecific: params.isVenueSpecific,
            }
          );

          return data.results;
        },
      })),
    });

    return userQueries;
  }

  getOrderActionViaModel(
    params: IGetEmployeeSlotSessionViaActionParams
  ): UseQueryResult<Array<IEmployeeOrderAction>, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: [
          "orders",
          "action-via-model",
          params.id,
          params.model,
          params.action,
        ],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      queryFn: async () => {
        const { id, model, action, isVenueSpecific = true } = params;

        if (!id || !model || !action) {
          return null;
        }

        const { data } = await this.api.get<
          AxiosResponse<IEmployeeOrderActionResponse>
        >(`/tracking/?pk=${id}&model=${model}&action=${action}`, {
          params: {
            page_size: 50,
          },
          endpointService: EndpointService.Core,
          venueSpecific: isVenueSpecific,
        });

        return data.results;
      },
    });
  }

  refundPayee(): UseMutationResult<void, Error, IRefundPayeeParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "full-refund-payee"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { order_identifier, payee_id, isVenueSpecific = true } = params;
        await this.api.delete<AxiosResponse<void>>(
          `/orders/${order_identifier}/payment/${payee_id}/refund/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: isVenueSpecific,
          }
        );
      },
      onMutate: async (params) => {
        const { order_identifier, payee_id } = params;

        // Optimistically update the payee in the cache
        await queryClient.cancelQueries({
          queryKey: ["orders", "get", order_identifier],
        });

        const previousOrder = queryClient.getQueryData<IOrderWithBill>([
          "orders",
          "get",
          order_identifier,
        ]);

        if (previousOrder) {
          // Update the payee's refunded status optimistically
          const updatedOrder = {
            ...previousOrder,
            payees: {
              ...previousOrder.payees,
              payees: previousOrder.payees.payees.map((payee) =>
                payee.id === payee_id ? { ...payee, refunded: true } : payee
              ),
            },
          };
          queryClient.setQueryData(
            ["orders", "get", order_identifier],
            updatedOrder
          );
        }

        return { previousOrder }; // Return the previous order for rollback if needed
      },
      onError: (err, params, context) => {
        // Rollback to the previous order data in case of error
        if (context?.previousOrder) {
          queryClient.setQueryData(
            ["orders", "get", params.order_identifier],
            context.previousOrder
          );
        }
      },
      onSettled: (data, error, params) => {
        // Refetch the order data after mutation
        queryClient.invalidateQueries({
          queryKey: ["orders", "get", params.order_identifier],
        });
      },
    });
  }

  customRefundPayee(): UseMutationResult<
    void,
    Error,
    ICustomRefundPayeeParams
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "custom-refund-payee"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const updatedParams = Object.fromEntries(
          Object.entries(params).filter(([key, value]) => value != null)
        );

        const {
          order_identifier,
          isVenueSpecific = true,
          ...restParams
        } = updatedParams;
        await this.api.post<AxiosResponse<void>>("/refunds/", {
          endpointService: EndpointService.Core,
          venueSpecific: isVenueSpecific,
          params: restParams,
        });
      },
      onMutate: async (params) => {},
      onError: async (err, params, context) => {},
      onSettled: async (data, error, params) => {
        queryClient.invalidateQueries({
          queryKey: ["orders", "get", params.order_identifier],
        });
      },
    });
  }

  refundOrder(): UseMutationResult<void, Error, IRefundOrderParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["orders", "refund-order"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { order_identifier, isVenueSpecific = true } = params;
        await this.api.post<AxiosResponse<void>>(
          `/orders/${order_identifier}/refund/`,
          {
            endpointService: EndpointService.Core,
            venueSpecific: isVenueSpecific,
          }
        );
      },
      onMutate: async (params) => {},
      onError: async (err, params, context) => {},
      onSettled: async (data, error, params) => {
        queryClient.invalidateQueries({
          queryKey: ["orders", "list"],
        });

        queryClient.invalidateQueries({
          queryKey: ["transactions", "list"],
        });
      },
    });
  }
}
